import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, ClockIcon, TagIcon, ArrowLeftIcon, ShareIcon } from '@heroicons/react/24/outline';
import SEO from '../components/SEO';

interface BlogPostData {
  id: number;
  title: string;
  excerpt: string;
  published_at: string;
  author: string;
  category: string;
  featured_image: string;
  read_time: string;
  tags: string[];
  content: string;
  slug: string;
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Fetch blog post from API
  useEffect(() => {
    const fetchBlogPost = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await fetch(`https://plencoplumbingltd.com/api/blogs/${slug}`);
        
        if (!response.ok) {
          throw new Error('Post not found');
        }
        
        const data = await response.json();
        setPost(data.data || data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load blog post');
        setPost(null);
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchBlogPost();
    }
  }, [slug]);

  // Full blog posts data with complete content (fallback)
  const blogPosts: { [key: string]: any } = {
    'commercial-plumbing-systems-uganda': {
      title: 'Complete Guide to Commercial Plumbing Systems in Uganda',
      excerpt: 'Discover essential considerations for commercial plumbing installations, from design to maintenance.',
      date: '2024-02-10',
      author: 'Plenco Engineering Team',
      category: 'Plumbing',
      image: '/images/projects/commercial-building-plumbing.jpg',
      readTime: '8 min read',
      tags: ['Commercial Plumbing', 'Building Systems', 'Water Management'],
      content: `
        <h2>Introduction to Commercial Plumbing Systems</h2>
        <p>Commercial plumbing systems are fundamentally different from residential installations. They must handle higher water demands, serve multiple floors, and comply with strict building codes and regulations. In Uganda's growing construction sector, understanding these systems is crucial for developers, contractors, and building managers.</p>

        <h2>Key Components of Commercial Plumbing</h2>
        <p>A comprehensive commercial plumbing system consists of several interconnected components:</p>
        <ul>
          <li><strong>Water Supply System:</strong> Includes main supply lines, pressure boosters, and distribution networks that ensure adequate water pressure across all floors and zones.</li>
          <li><strong>Drainage System:</strong> Designed to handle high-volume wastewater from multiple fixtures, including specialized drainage for kitchens, bathrooms, and industrial areas.</li>
          <li><strong>Ventilation:</strong> Critical for maintaining proper drainage flow and preventing sewer gas buildup in the building.</li>
          <li><strong>Fire Suppression:</strong> Integrated sprinkler systems connected to dedicated water supplies for safety compliance.</li>
        </ul>

        <h2>Design Considerations for Uganda's Climate</h2>
        <p>Uganda's tropical climate presents unique challenges for plumbing systems. High humidity levels can accelerate corrosion, while temperature variations affect pipe expansion. Our engineers account for these factors by:</p>
        <ul>
          <li>Selecting corrosion-resistant materials like PVC, CPVC, and stainless steel</li>
          <li>Installing proper insulation where needed</li>
          <li>Designing systems with adequate expansion joints</li>
          <li>Implementing water treatment solutions to address hard water issues common in East Africa</li>
        </ul>

        <h2>Pipe Materials and Selection</h2>
        <p>Choosing the right pipe materials is crucial for longevity and performance. For commercial buildings in Uganda, we typically recommend:</p>
        <ul>
          <li><strong>CPVC (Chlorinated Polyvinyl Chloride):</strong> Excellent for hot and cold water distribution, resistant to corrosion and chemical degradation.</li>
          <li><strong>PVC (Polyvinyl Chloride):</strong> Ideal for drainage and waste systems, cost-effective and durable.</li>
          <li><strong>Steel Pipes:</strong> For high-pressure applications and fire suppression systems.</li>
          <li><strong>HDPE (High-Density Polyethylene):</strong> Perfect for underground water supply lines.</li>
        </ul>

        <h2>Water Pressure Management</h2>
        <p>Multi-story commercial buildings require sophisticated pressure management systems. We design and install:</p>
        <ul>
          <li>Variable speed pressure booster pumps for consistent water pressure</li>
          <li>Pressure reducing valves to protect fixtures from excessive pressure</li>
          <li>Zone-specific pressure regulation for optimal performance</li>
          <li>Backup systems to ensure continuous operation</li>
        </ul>

        <h2>Regulatory Compliance in East Africa</h2>
        <p>All our commercial plumbing installations comply with Uganda National Bureau of Standards (UNBS) requirements and international plumbing codes. This includes proper permitting, inspections, and documentation throughout the project lifecycle.</p>

        <h2>Maintenance Best Practices</h2>
        <p>Regular maintenance is essential for commercial plumbing systems. We recommend:</p>
        <ul>
          <li>Quarterly inspections of all visible pipes, fixtures, and connections</li>
          <li>Annual professional servicing of pumps and pressure systems</li>
          <li>Immediate attention to any leaks or pressure irregularities</li>
          <li>Water quality testing every six months</li>
          <li>Cleaning of drainage systems to prevent blockages</li>
        </ul>

        <h2>Cost Considerations</h2>
        <p>While commercial plumbing represents a significant investment, proper planning and quality installation reduce long-term costs. Our approach focuses on:</p>
        <ul>
          <li>Right-sizing systems to avoid over-specification</li>
          <li>Using durable materials that minimize maintenance</li>
          <li>Implementing water-efficient fixtures to reduce operating costs</li>
          <li>Designing for future expansion and modifications</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Commercial plumbing systems in Uganda require expert design, quality materials, and professional installation. At Plenco, we bring over 15 years of experience to every project, ensuring your building has reliable plumbing infrastructure that stands the test of time.</p>

        <p><strong>Ready to start your commercial plumbing project?</strong> Contact our engineering team today for a comprehensive consultation and detailed quotation.</p>
      `
    },
    'tank-calibration-best-practices': {
      title: 'Tank Calibration Best Practices for Fuel Stations',
      excerpt: 'Understanding the critical importance of accurate tank calibration in the petroleum industry.',
      date: '2024-02-08',
      author: 'Technical Calibration Team',
      category: 'Tank Calibration',
      image: '/images/projects/tank-calibration.jpg',
      readTime: '6 min read',
      tags: ['Tank Calibration', 'Fuel Stations', 'Industry Standards'],
      content: `
        <h2>Understanding Tank Calibration</h2>
        <p>Tank calibration is the process of determining the exact volume of liquid a storage tank can hold at various liquid levels. For fuel stations across Uganda and East Africa, accurate calibration is not just a regulatory requirement—it's essential for inventory management, financial accountability, and customer trust.</p>

        <h2>Why Tank Calibration Matters</h2>
        <p>Accurate tank calibration directly impacts your business operations:</p>
        <ul>
          <li><strong>Financial Accuracy:</strong> Ensures precise inventory valuation and prevents losses from measurement errors</li>
          <li><strong>Regulatory Compliance:</strong> Meets Uganda Bureau of Standards (UNBS) requirements and international petroleum standards</li>
          <li><strong>Customer Trust:</strong> Demonstrates commitment to fair and accurate fuel dispensing</li>
          <li><strong>Loss Prevention:</strong> Helps identify leaks, theft, or evaporation through accurate volume tracking</li>
        </ul>

        <h2>Calibration Methods</h2>
        <p>We employ multiple calibration methods depending on tank type and client requirements:</p>
        
        <h3>1. Strapping Method</h3>
        <p>The most common method for cylindrical tanks, involving precise external measurements and calculations based on tank geometry. This method is ideal for both horizontal and vertical storage tanks.</p>

        <h3>2. Internal Measurement Method</h3>
        <p>Involves direct internal measurements of the tank using specialized equipment. This method accounts for internal structures, irregularities, and bottom contours.</p>

        <h3>3. Volumetric Method</h3>
        <p>Uses calibrated liquid volumes to determine tank capacity. This method is particularly useful for irregular tanks or validation of other calibration methods.</p>

        <h2>Our Calibration Process</h2>
        <p>At Plenco, we follow a rigorous calibration procedure:</p>
        <ol>
          <li><strong>Pre-Calibration Inspection:</strong> Thorough examination of tank condition, identifying any deformities or damage</li>
          <li><strong>Measurement:</strong> Precise measurements using calibrated instruments and following international standards</li>
          <li><strong>Data Processing:</strong> Advanced calculations accounting for temperature, pressure, and tank material properties</li>
          <li><strong>Table Generation:</strong> Creation of comprehensive calibration tables (strapping charts)</li>
          <li><strong>Documentation:</strong> Complete certification package including methodology, measurements, and calculations</li>
        </ol>

        <h2>Equipment and Technology</h2>
        <p>We utilize state-of-the-art calibration equipment:</p>
        <ul>
          <li>High-precision measuring tapes with temperature compensation</li>
          <li>Electronic distance measurement devices</li>
          <li>Level sensors and gauging systems</li>
          <li>Advanced software for data analysis and table generation</li>
          <li>Temperature and density measurement instruments</li>
        </ul>

        <h2>Calibration Frequency</h2>
        <p>Industry best practices and regulatory requirements typically mandate:</p>
        <ul>
          <li><strong>New Installations:</strong> Initial calibration before commissioning</li>
          <li><strong>Regular Recalibration:</strong> Every 5 years for underground tanks, 10 years for above-ground tanks</li>
          <li><strong>Post-Repair Calibration:</strong> After any structural repairs or modifications</li>
          <li><strong>Incident-Triggered:</strong> Following any accident, natural disaster, or suspected damage</li>
        </ul>

        <h2>Regulatory Framework in Uganda</h2>
        <p>Tank calibration in Uganda must comply with:</p>
        <ul>
          <li>Uganda National Bureau of Standards (UNBS) regulations</li>
          <li>Petroleum Supply Act requirements</li>
          <li>API (American Petroleum Institute) standards</li>
          <li>ISO 7507 international standards for petroleum measurement</li>
        </ul>

        <h2>Common Calibration Challenges</h2>
        <p>Our experienced team addresses various challenges:</p>
        <ul>
          <li>Irregular tank shapes and internal structures</li>
          <li>Tank deformation over time due to pressure or settlement</li>
          <li>Access limitations for measurement</li>
          <li>Environmental factors affecting measurements</li>
          <li>Integration with existing monitoring systems</li>
        </ul>

        <h2>Benefits of Professional Calibration</h2>
        <p>Partnering with Plenco for tank calibration ensures:</p>
        <ul>
          <li>Certified results accepted by regulatory authorities</li>
          <li>Accurate calibration tables for daily operations</li>
          <li>Comprehensive documentation for audits</li>
          <li>Expert recommendations for system improvements</li>
          <li>Ongoing technical support</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Tank calibration is a critical service that underpins the entire petroleum supply chain. With Plenco's expertise and commitment to accuracy, you can trust that your fuel storage measurements meet the highest standards of precision and compliance.</p>

        <p><strong>Need tank calibration services?</strong> Contact us today to schedule an assessment and receive a detailed quotation for your facility.</p>
      `
    },
    'electrical-installation-techniques': {
      title: 'Modern Electrical Installation Techniques for Commercial Buildings',
      excerpt: 'Explore cutting-edge electrical installation methods for commercial and industrial facilities.',
      date: '2024-02-05',
      author: 'Electrical Services Division',
      category: 'Electrical',
      image: '/images/projects/electrical-transformer.jpg',
      readTime: '7 min read',
      tags: ['Electrical Installation', 'Transformers', 'Power Distribution'],
      content: `
        <h2>The Evolution of Commercial Electrical Systems</h2>
        <p>Modern commercial buildings in Uganda require sophisticated electrical systems that go far beyond basic wiring. Today's installations must support advanced technology, ensure energy efficiency, and provide reliable power distribution across complex facilities.</p>

        <h2>Transformer Installation Fundamentals</h2>
        <p>Transformers are the heart of commercial electrical distribution. Our installation process includes:</p>
        <ul>
          <li><strong>Site Assessment:</strong> Evaluating load requirements, space availability, and environmental conditions</li>
          <li><strong>Transformer Selection:</strong> Choosing appropriate capacity, voltage ratings, and efficiency levels</li>
          <li><strong>Foundation Preparation:</strong> Creating stable, level platforms that meet manufacturer specifications</li>
          <li><strong>Connection and Testing:</strong> Proper wiring, grounding, and comprehensive commissioning tests</li>
        </ul>

        <h2>Power Distribution Architecture</h2>
        <p>Effective power distribution requires careful planning:</p>
        <h3>Main Distribution Boards (MDBs)</h3>
        <p>Centralized distribution points that receive power from transformers and route it to sub-distribution boards throughout the building.</p>

        <h3>Sub-Distribution Boards (SDBs)</h3>
        <p>Secondary distribution points that serve specific zones or floors, allowing for localized control and protection.</p>

        <h3>Circuit Protection</h3>
        <p>Multi-tiered protection using circuit breakers, RCDs, and surge protectors to ensure safety and prevent damage from electrical faults.</p>

        <h2>High Voltage vs. Low Voltage Systems</h2>
        <p>Understanding the distinction is crucial:</p>
        <ul>
          <li><strong>High Voltage (HV):</strong> Typically 11kV or higher, used for incoming power from the grid and internal distribution in large facilities</li>
          <li><strong>Low Voltage (LV):</strong> 400V/230V systems for final distribution to equipment and outlets</li>
        </ul>

        <h2>Cabling and Conduit Systems</h2>
        <p>Modern installations use various cable types:</p>
        <ul>
          <li><strong>Armored Cables:</strong> For underground runs and mechanical protection</li>
          <li><strong>PVC Insulated Cables:</strong> Cost-effective for conduit installations</li>
          <li><strong>Fire-Resistant Cables:</strong> Essential for life safety systems and emergency circuits</li>
          <li><strong>Data and Communication Cables:</strong> Structured cabling for IT infrastructure</li>
        </ul>

        <h2>Grounding and Earthing Systems</h2>
        <p>Proper grounding protects people and equipment:</p>
        <ul>
          <li>Lightning protection systems for tall buildings</li>
          <li>Equipment grounding for all electrical panels and metal enclosures</li>
          <li>Separate grounding for sensitive electronic equipment</li>
          <li>Regular testing and maintenance of earth connections</li>
        </ul>

        <h2>Energy Efficiency Measures</h2>
        <p>Modern electrical installations incorporate efficiency features:</p>
        <ul>
          <li><strong>LED Lighting Systems:</strong> Reducing energy consumption by up to 80% compared to traditional lighting</li>
          <li><strong>Occupancy Sensors:</strong> Automatic control of lighting and HVAC in unoccupied spaces</li>
          <li><strong>Power Factor Correction:</strong> Improving overall system efficiency and reducing utility costs</li>
          <li><strong>Energy Monitoring:</strong> Real-time tracking of consumption patterns for optimization</li>
        </ul>

        <h2>Smart Building Integration</h2>
        <p>Contemporary electrical systems support building automation:</p>
        <ul>
          <li>Centralized control systems for lighting, HVAC, and security</li>
          <li>Remote monitoring and management capabilities</li>
          <li>Integration with renewable energy sources</li>
          <li>Automated load management and demand response</li>
        </ul>

        <h2>Safety Standards and Compliance</h2>
        <p>All our installations meet:</p>
        <ul>
          <li>IEC (International Electrotechnical Commission) standards</li>
          <li>Uganda Electricity Regulatory Authority (ERA) requirements</li>
          <li>Building codes and local authority regulations</li>
          <li>Occupational safety and health requirements</li>
        </ul>

        <h2>Emergency and Backup Power</h2>
        <p>Critical facilities require reliable backup systems:</p>
        <ul>
          <li>Generator installations with automatic transfer switches</li>
          <li>UPS (Uninterruptible Power Supply) systems for sensitive equipment</li>
          <li>Emergency lighting systems with battery backup</li>
          <li>Fire alarm and life safety system power protection</li>
        </ul>

        <h2>Testing and Commissioning</h2>
        <p>Comprehensive testing ensures safe operation:</p>
        <ul>
          <li>Insulation resistance testing</li>
          <li>Earth continuity verification</li>
          <li>Load testing and balancing</li>
          <li>Protection device calibration</li>
          <li>System performance documentation</li>
        </ul>

        <h2>Maintenance Programs</h2>
        <p>Regular maintenance prevents failures and extends system life:</p>
        <ul>
          <li>Annual thermographic inspections to identify hot spots</li>
          <li>Quarterly visual inspections of accessible equipment</li>
          <li>Periodic testing of protection devices</li>
          <li>Cleaning and tightening of connections</li>
          <li>Documentation and trending of system performance</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Modern electrical installation requires expertise, quality materials, and adherence to international standards. Plenco's electrical services division brings decades of combined experience to every project, ensuring your facility has safe, reliable, and efficient power distribution.</p>

        <p><strong>Planning an electrical installation?</strong> Contact our electrical engineering team for a detailed assessment and customized solution for your facility.</p>
      `
    },
    'residential-plumbing-installation-guide': {
      title: 'Residential Plumbing Installation: From Planning to Completion',
      excerpt: 'A comprehensive walkthrough of residential plumbing projects from initial planning to final inspection.',
      date: '2024-02-01',
      author: 'Residential Projects Manager',
      category: 'Residential',
      image: '/images/projects/plumbing-residential-apartment.webp',
      readTime: '9 min read',
      tags: ['Residential Plumbing', 'Apartment Buildings', 'Installation Guide'],
      content: `
        <h2>Understanding Residential Plumbing Systems</h2>
        <p>Residential plumbing systems, whether for individual homes or apartment complexes, form the foundation of comfortable living. A well-designed system provides reliable water supply, efficient drainage, and years of trouble-free operation.</p>

        <h2>Planning Phase: Critical First Steps</h2>
        <p>Successful plumbing projects begin with thorough planning:</p>
        <ul>
          <li><strong>Building Blueprints Review:</strong> Understanding the architectural layout and structural elements</li>
          <li><strong>Water Demand Calculation:</strong> Determining peak demand based on number of fixtures and occupants</li>
          <li><strong>Pressure Requirements:</strong> Calculating necessary pressure for all floors and fixtures</li>
          <li><strong>Code Compliance:</strong> Ensuring designs meet local building codes and regulations</li>
        </ul>

        <h2>Water Supply System Design</h2>
        <p>The supply system delivers clean water throughout the building:</p>
        <h3>Main Supply Line</h3>
        <p>Sized appropriately to handle peak demand while maintaining adequate pressure. For apartment buildings, this typically ranges from 1.5" to 3" depending on unit count.</p>

        <h3>Vertical Risers</h3>
        <p>Dedicated pipes that carry water vertically to upper floors, with branches to each level. Proper sizing prevents pressure loss and ensures even distribution.</p>

        <h3>Horizontal Distribution</h3>
        <p>Branch lines serving individual apartments or rooms, typically using 0.5" to 1" pipes depending on fixture groups.</p>

        <h2>Drainage System Architecture</h2>
        <p>Effective drainage removes wastewater efficiently and prevents backups:</p>
        <ul>
          <li><strong>Soil Stacks:</strong> Main vertical drainage pipes collecting waste from toilets and other fixtures</li>
          <li><strong>Waste Stacks:</strong> Separate vertical drains for sinks, showers, and washing machines</li>
          <li><strong>Branch Drains:</strong> Horizontal pipes connecting fixtures to vertical stacks</li>
          <li><strong>Venting System:</strong> Critical for maintaining proper drainage flow and preventing sewer gas entry</li>
        </ul>

        <h2>Fixture Selection and Installation</h2>
        <p>Choosing appropriate fixtures impacts both functionality and water efficiency:</p>
        <h3>Toilets</h3>
        <p>Modern dual-flush toilets reduce water consumption while maintaining performance. Proper installation with correct wax ring sealing prevents leaks and odors.</p>

        <h3>Sinks and Faucets</h3>
        <p>Quality fixtures with aerators reduce water use without sacrificing user experience. Proper mounting and sealing prevent counter damage.</p>

        <h3>Showers and Bathtubs</h3>
        <p>Waterproofing is critical. We use multiple layers of protection including membranes, proper slope, and quality sealants.</p>

        <h2>Hot Water Systems</h2>
        <p>Reliable hot water supply requires careful system design:</p>
        <ul>
          <li><strong>Water Heater Sizing:</strong> Calculating capacity based on peak simultaneous usage</li>
          <li><strong>Energy Efficiency:</strong> Selecting appropriate heater type (electric, gas, solar, or hybrid)</li>
          <li><strong>Distribution Design:</strong> Minimizing pipe runs to reduce heat loss and wait times</li>
          <li><strong>Insulation:</strong> Protecting hot water pipes to maintain temperature and efficiency</li>
        </ul>

        <h2>Pipe Materials for Residential Applications</h2>
        <p>We select materials based on application and local water conditions:</p>
        <ul>
          <li><strong>CPVC:</strong> Excellent for hot and cold water distribution, easy to install, corrosion-resistant</li>
          <li><strong>PEX (Cross-linked Polyethylene):</strong> Flexible, freeze-resistant, ideal for complex routing</li>
          <li><strong>PVC:</strong> Standard for drainage and vent systems, cost-effective and durable</li>
          <li><strong>Copper:</strong> Premium choice for water supply, long-lasting but more expensive</li>
        </ul>

        <h2>Pressure Management</h2>
        <p>Maintaining consistent water pressure across all units:</p>
        <ul>
          <li>Pressure booster pumps for buildings over three stories</li>
          <li>Pressure reducing valves to protect fixtures from excessive pressure</li>
          <li>Individual apartment shut-off valves for maintenance isolation</li>
          <li>Air chambers or water hammer arrestors to eliminate pipe noise</li>
        </ul>

        <h2>Waterproofing and Protection</h2>
        <p>Critical areas require special attention:</p>
        <ul>
          <li><strong>Bathroom Floors:</strong> Multiple waterproof membrane layers beneath tiles</li>
          <li><strong>Shower Areas:</strong> Complete waterproof enclosures from floor to ceiling</li>
          <li><strong>Kitchen Backsplashes:</strong> Protection against water damage behind sinks and counters</li>
          <li><strong>Pipe Penetrations:</strong> Proper sealing where pipes pass through walls and floors</li>
        </ul>

        <h2>Quality Control and Testing</h2>
        <p>Our comprehensive testing process includes:</p>
        <ol>
          <li><strong>Pressure Testing:</strong> All supply lines tested at 1.5x operating pressure for 24 hours</li>
          <li><strong>Drainage Testing:</strong> Water and smoke tests to verify proper flow and venting</li>
          <li><strong>Fixture Operation:</strong> Testing all fixtures under normal operating conditions</li>
          <li><strong>Leak Detection:</strong> Thorough inspection of all connections and joints</li>
          <li><strong>Documentation:</strong> Comprehensive as-built drawings and test certificates</li>
        </ol>

        <h2>Energy and Water Efficiency</h2>
        <p>Modern residential plumbing incorporates efficiency features:</p>
        <ul>
          <li>Low-flow fixtures reducing water consumption by 30-50%</li>
          <li>Efficient water heaters with proper insulation</li>
          <li>Greywater systems for garden irrigation where applicable</li>
          <li>Leak detection systems to identify problems early</li>
        </ul>

        <h2>Common Installation Challenges</h2>
        <p>Our experience addresses typical issues:</p>
        <ul>
          <li><strong>Space Constraints:</strong> Creative routing solutions for tight spaces</li>
          <li><strong>Existing Structure Integration:</strong> Working with existing buildings and retrofits</li>
          <li><strong>Coordination:</strong> Timing with other trades (electrical, HVAC, flooring)</li>
          <li><strong>Material Availability:</strong> Sourcing quality materials in local markets</li>
        </ul>

        <h2>Maintenance Recommendations</h2>
        <p>Educating residents on maintenance prevents problems:</p>
        <ul>
          <li>Regular inspection of visible pipes and connections</li>
          <li>Prompt attention to drips and slow drains</li>
          <li>Annual water heater maintenance</li>
          <li>Proper use of fixtures to prevent damage</li>
          <li>Awareness of what not to flush or drain</li>
        </ul>

        <h2>Warranty and Support</h2>
        <p>Plenco stands behind our work:</p>
        <ul>
          <li>Comprehensive warranty on workmanship</li>
          <li>Manufacturer warranties on fixtures and equipment</li>
          <li>Emergency service availability</li>
          <li>Ongoing technical support for residents and property managers</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Residential plumbing installation requires expertise, quality materials, and attention to detail. Whether you're building a single home or a multi-unit complex, Plenco delivers reliable systems that serve residents well for decades.</p>

        <p><strong>Planning a residential plumbing project?</strong> Contact us for a detailed consultation and competitive quotation tailored to your specific needs.</p>
      `
    },
    'industrial-piping-systems-guide': {
      title: 'Industrial Piping Systems: Design and Installation Excellence',
      excerpt: 'Master the complexities of industrial piping installations for manufacturing facilities.',
      date: '2024-01-28',
      author: 'Industrial Engineering Team',
      category: 'Industrial',
      image: '/images/projects/industrial-piping-systems.jpg',
      readTime: '10 min read',
      tags: ['Industrial Piping', 'Manufacturing', 'System Design'],
      content: `
        <h2>Introduction to Industrial Piping Systems</h2>
        <p>Industrial piping systems are the circulatory system of manufacturing facilities, transporting raw materials, process fluids, compressed air, and waste products throughout the plant. Unlike residential or commercial plumbing, industrial piping must handle extreme pressures, temperatures, and corrosive materials while maintaining safety and efficiency.</p>

        <h2>Types of Industrial Piping Systems</h2>
        <p>Manufacturing facilities typically require multiple specialized piping systems:</p>
        <ul>
          <li><strong>Process Piping:</strong> Transports raw materials and intermediate products through production processes</li>
          <li><strong>Utility Piping:</strong> Distributes steam, compressed air, cooling water, and other utilities</li>
          <li><strong>Waste Piping:</strong> Handles industrial effluents and wastewater</li>
          <li><strong>Fire Protection:</strong> Dedicated systems for fire suppression and safety</li>
        </ul>

        <h2>Design Phase: Engineering Excellence</h2>
        <p>Industrial piping design requires comprehensive engineering:</p>
        <h3>Process Analysis</h3>
        <p>Understanding the fluids being transported, including temperature, pressure, viscosity, and chemical properties.</p>

        <h3>Flow Calculations</h3>
        <p>Determining required flow rates and sizing pipes to minimize pressure drop while maintaining velocity within acceptable ranges.</p>

        <h3>Material Selection</h3>
        <p>Choosing appropriate materials based on fluid characteristics, temperature extremes, and corrosion resistance requirements.</p>

        <h3>Layout Planning</h3>
        <p>Designing efficient routing that minimizes pipe length, reduces fittings, and provides access for maintenance.</p>

        <h2>Pipe Materials for Industrial Applications</h2>
        <p>Material selection is critical for industrial systems:</p>
        <ul>
          <li><strong>Carbon Steel:</strong> Most common for general industrial use, cost-effective for water, air, and non-corrosive fluids</li>
          <li><strong>Stainless Steel:</strong> Superior corrosion resistance for food processing, pharmaceuticals, and chemical industries</li>
          <li><strong>PVC and CPVC:</strong> Chemical resistance for corrosive fluids at moderate temperatures</li>
          <li><strong>HDPE:</strong> Excellent for underground installations and corrosive environments</li>
          <li><strong>Specialized Alloys:</strong> For extreme conditions including titanium, Hastelloy, and duplex stainless steels</li>
        </ul>

        <h2>Welding and Joining Methods</h2>
        <p>Industrial piping connections require certified techniques:</p>
        <h3>Welding</h3>
        <p>Primary method for permanent connections in steel piping. We employ certified welders using appropriate techniques (SMAW, GTAW, GMAW) based on material and application.</p>

        <h3>Flanged Connections</h3>
        <p>Used where disassembly may be required. Proper gasket selection and bolt tightening procedures ensure leak-free operation.</p>

        <h3>Threaded Connections</h3>
        <p>For smaller diameter pipes and where welding is impractical. Requires proper thread sealants and installation torque.</p>

        <h2>Support and Hanger Systems</h2>
        <p>Proper support prevents stress and failure:</p>
        <ul>
          <li><strong>Pipe Hangers:</strong> Support vertical loads while allowing thermal expansion</li>
          <li><strong>Rigid Supports:</strong> Anchor points preventing movement in specific directions</li>
          <li><strong>Spring Supports:</strong> Accommodate vertical thermal movement while maintaining support</li>
          <li><strong>Vibration Isolation:</strong> Specialized supports for equipment with reciprocating or rotating components</li>
        </ul>

        <h2>Pressure Testing and Inspection</h2>
        <p>Rigorous testing ensures system integrity:</p>
        <ol>
          <li><strong>Hydrostatic Testing:</strong> Pressurizing with water at 1.5x design pressure to verify strength</li>
          <li><strong>Pneumatic Testing:</strong> Using air for systems that cannot be filled with liquid</li>
          <li><strong>Radiographic Inspection:</strong> X-ray examination of critical welds</li>
          <li><strong>Ultrasonic Testing:</strong> Non-destructive examination of material thickness and weld quality</li>
          <li><strong>Dye Penetrant Testing:</strong> Surface crack detection in welds and base material</li>
        </ol>

        <h2>Insulation Systems</h2>
        <p>Thermal insulation serves multiple purposes:</p>
        <ul>
          <li>Energy conservation for hot or cold process lines</li>
          <li>Personnel protection from hot surfaces</li>
          <li>Condensation prevention on cold piping</li>
          <li>Process temperature maintenance</li>
        </ul>

        <h2>Safety Considerations</h2>
        <p>Industrial piping safety is paramount:</p>
        <ul>
          <li><strong>Pressure Relief:</strong> Safety valves sized and installed per ASME standards</li>
          <li><strong>Emergency Shutdown:</strong> Strategically located isolation valves</li>
          <li><strong>Leak Detection:</strong> Monitoring systems for hazardous materials</li>
          <li><strong>Identification:</strong> Color coding and labeling per international standards</li>
          <li><strong>Access and Egress:</strong> Maintaining safe pathways and escape routes</li>
        </ul>

        <h2>Corrosion Protection</h2>
        <p>Extending system life through corrosion management:</p>
        <ul>
          <li>Proper material selection resistant to process fluids</li>
          <li>Protective coatings for external surfaces</li>
          <li>Cathodic protection for underground piping</li>
          <li>Corrosion allowances in design thickness</li>
          <li>Regular inspection and monitoring programs</li>
        </ul>

        <h2>Valve Selection and Installation</h2>
        <p>Industrial valves must match application requirements:</p>
        <ul>
          <li><strong>Gate Valves:</strong> Full flow isolation service</li>
          <li><strong>Globe Valves:</strong> Throttling and flow control</li>
          <li><strong>Ball Valves:</strong> Quick shut-off with minimal pressure drop</li>
          <li><strong>Butterfly Valves:</strong> Large diameter applications with space constraints</li>
          <li><strong>Check Valves:</strong> Preventing backflow and protecting equipment</li>
        </ul>

        <h2>Automation and Control</h2>
        <p>Modern systems incorporate intelligent control:</p>
        <ul>
          <li>Automated valves with electric or pneumatic actuators</li>
          <li>Flow measurement and monitoring</li>
          <li>Pressure and temperature sensors</li>
          <li>Integration with SCADA systems</li>
          <li>Remote operation and diagnostics</li>
        </ul>

        <h2>Compliance and Standards</h2>
        <p>Our installations meet international codes:</p>
        <ul>
          <li>ASME B31.3 Process Piping Code</li>
          <li>ASME B31.1 Power Piping Code</li>
          <li>API (American Petroleum Institute) standards</li>
          <li>Local regulatory requirements</li>
          <li>Industry-specific standards (pharmaceutical, food processing, etc.)</li>
        </ul>

        <h2>Documentation and As-Built Drawings</h2>
        <p>Comprehensive documentation ensures operational success:</p>
        <ul>
          <li>Detailed piping and instrumentation diagrams (P&IDs)</li>
          <li>Isometric drawings showing exact pipe routing</li>
          <li>Material specifications and mill test reports</li>
          <li>Weld maps and inspection records</li>
          <li>Test certificates and commissioning reports</li>
        </ul>

        <h2>Maintenance and Lifecycle Management</h2>
        <p>Proactive maintenance prevents costly failures:</p>
        <ul>
          <li><strong>Inspection Programs:</strong> Regular visual and instrumental examination</li>
          <li><strong>Predictive Maintenance:</strong> Monitoring for early signs of degradation</li>
          <li><strong>Corrosion Management:</strong> Thickness measurements and protective coating inspection</li>
          <li><strong>Valve Maintenance:</strong> Regular testing and refurbishment</li>
          <li><strong>Spare Parts Management:</strong> Maintaining critical components inventory</li>
        </ul>

        <h2>Environmental Considerations</h2>
        <p>Responsible industrial piping includes environmental protection:</p>
        <ul>
          <li>Secondary containment for hazardous materials</li>
          <li>Leak detection and prevention systems</li>
          <li>Proper disposal of process fluids</li>
          <li>Compliance with environmental regulations</li>
          <li>Spill response preparedness</li>
        </ul>

        <h2>Project Management Excellence</h2>
        <p>Successful installation requires meticulous coordination:</p>
        <ul>
          <li>Detailed scheduling and progress tracking</li>
          <li>Quality control at every phase</li>
          <li>Coordination with other trades</li>
          <li>Safety management and training</li>
          <li>Budget control and reporting</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Industrial piping systems demand the highest levels of engineering expertise, quality materials, and skilled installation. Plenco's industrial division brings decades of experience to complex manufacturing facilities, ensuring reliable, safe, and efficient process systems.</p>

        <p><strong>Planning an industrial piping project?</strong> Contact our industrial engineering team for a comprehensive consultation and detailed engineering proposal tailored to your facility's unique requirements.</p>
      `
    },
    'water-treatment-plant-installation': {
      title: 'Water Treatment Plant Installation and Maintenance in East Africa',
      excerpt: 'Essential knowledge for water treatment infrastructure projects across East Africa.',
      date: '2024-01-25',
      author: 'Infrastructure Development Team',
      category: 'Infrastructure',
      image: '/images/projects/water-treatment-plant.jpg',
      readTime: '11 min read',
      tags: ['Water Treatment', 'Infrastructure', 'Public Health'],
      content: `
        <h2>The Importance of Clean Water Infrastructure</h2>
        <p>Access to clean, safe water is fundamental to public health and economic development. In East Africa, where water quality challenges are common, professional water treatment infrastructure is essential. Plenco specializes in designing, installing, and maintaining water treatment facilities that serve communities and industries across Uganda and the region.</p>

        <h2>Water Quality Challenges in East Africa</h2>
        <p>Understanding local water quality issues guides treatment system design:</p>
        <ul>
          <li><strong>Biological Contamination:</strong> Bacteria, viruses, and parasites from untreated wastewater and agricultural runoff</li>
          <li><strong>Turbidity:</strong> Suspended solids making water cloudy, common during rainy seasons</li>
          <li><strong>Chemical Contaminants:</strong> Heavy metals, pesticides, and industrial pollutants</li>
          <li><strong>Hardness:</strong> High mineral content causing scaling and taste issues</li>
          <li><strong>Iron and Manganese:</strong> Natural minerals causing discoloration and metallic taste</li>
        </ul>

        <h2>Water Treatment Processes</h2>
        <p>Comprehensive treatment involves multiple stages:</p>
        <h3>1. Coagulation and Flocculation</h3>
        <p>Chemical coagulants bind suspended particles into larger flocs that settle more easily. This critical first step removes turbidity and many microorganisms.</p>

        <h3>2. Sedimentation</h3>
        <p>Flocculated particles settle in large basins, separating from the water. Proper basin design ensures adequate detention time for effective settling.</p>

        <h3>3. Filtration</h3>
        <p>Multiple filtration stages remove remaining suspended solids:</p>
        <ul>
          <li><strong>Sand Filters:</strong> Rapid or slow sand filtration for mechanical removal</li>
          <li><strong>Multimedia Filters:</strong> Layers of different media for improved efficiency</li>
          <li><strong>Activated Carbon:</strong> Removes organic compounds, taste, and odor</li>
        </ul>

        <h3>4. Disinfection</h3>
        <p>Final treatment eliminates remaining microorganisms:</p>
        <ul>
          <li><strong>Chlorination:</strong> Most common, cost-effective method</li>
          <li><strong>UV Treatment:</strong> Chemical-free disinfection</li>
          <li><strong>Ozonation:</strong> Powerful oxidation for advanced treatment</li>
        </ul>

        <h2>Treatment Plant Design Considerations</h2>
        <p>Effective design accounts for multiple factors:</p>
        <ul>
          <li><strong>Capacity Planning:</strong> Current demand plus growth projections</li>
          <li><strong>Raw Water Quality:</strong> Seasonal variations and worst-case scenarios</li>
          <li><strong>Treated Water Standards:</strong> WHO guidelines and local regulations</li>
          <li><strong>Site Conditions:</strong> Topography, soil types, and environmental factors</li>
          <li><strong>Reliability:</strong> Redundant equipment and backup systems</li>
        </ul>

        <h2>Key Treatment Plant Components</h2>
        <p>Modern plants integrate sophisticated systems:</p>
        <h3>Intake Systems</h3>
        <p>Raw water collection from rivers, lakes, or boreholes with screens to prevent debris entry.</p>

        <h3>Pumping Stations</h3>
        <p>Reliable pumps with variable speed drives for efficient operation and backup units for continuity.</p>

        <h3>Chemical Dosing Systems</h3>
        <p>Automated systems precisely adding coagulants, disinfectants, and pH adjustment chemicals.</p>

        <h3>Clarification Basins</h3>
        <p>Designed for optimal hydraulic flow and settling efficiency with sludge removal systems.</p>

        <h3>Filter Systems</h3>
        <p>Multiple filter units allowing continuous operation while individual filters undergo backwashing.</p>

        <h3>Clear Water Reservoirs</h3>
        <p>Storage tanks maintaining supply during demand peaks and providing contact time for disinfection.</p>

        <h2>Automation and Monitoring</h2>
        <p>Modern plants employ advanced control systems:</p>
        <ul>
          <li><strong>SCADA Systems:</strong> Centralized monitoring and control of all plant operations</li>
          <li><strong>Real-Time Monitoring:</strong> Continuous measurement of water quality parameters</li>
          <li><strong>Automated Dosing:</strong> Computer-controlled chemical addition based on water quality</li>
          <li><strong>Remote Access:</strong> Operation and monitoring from off-site locations</li>
          <li><strong>Alarm Systems:</strong> Immediate notification of operational issues</li>
        </ul>

        <h2>Quality Assurance and Laboratory Testing</h2>
        <p>Continuous quality verification ensures safe water:</p>
        <ul>
          <li>On-line instruments measuring pH, turbidity, chlorine residual</li>
          <li>Regular laboratory testing for bacteriological quality</li>
          <li>Periodic comprehensive analysis for chemical parameters</li>
          <li>Comparison with WHO guidelines and national standards</li>
          <li>Documentation and reporting requirements</li>
        </ul>

        <h2>Energy Efficiency Measures</h2>
        <p>Reducing operational costs through efficient design:</p>
        <ul>
          <li>Variable frequency drives on pumps and motors</li>
          <li>Gravity flow where topography permits</li>
          <li>Energy-efficient lighting and equipment</li>
          <li>Solar power integration for suitable applications</li>
          <li>Process optimization to minimize energy use</li>
        </ul>

        <h2>Sludge Management</h2>
        <p>Proper handling of treatment residuals:</p>
        <ul>
          <li><strong>Sludge Collection:</strong> Regular removal from clarification basins</li>
          <li><strong>Thickening:</strong> Concentration to reduce volume</li>
          <li><strong>Dewatering:</strong> Further water removal using drying beds or mechanical systems</li>
          <li><strong>Disposal:</strong> Environmentally responsible management</li>
        </ul>

        <h2>Regulatory Compliance</h2>
        <p>Meeting water quality standards and regulations:</p>
        <ul>
          <li>WHO Water Quality Guidelines</li>
          <li>Uganda National Bureau of Standards (UNBS) specifications</li>
          <li>National Water Quality Monitoring Framework</li>
          <li>Environmental impact assessment requirements</li>
          <li>Regular reporting to regulatory authorities</li>
        </ul>

        <h2>Operation and Maintenance</h2>
        <p>Sustainable operation requires comprehensive programs:</p>
        <h3>Daily Operations</h3>
        <p>Routine monitoring, chemical dosing adjustments, and filter backwashing performed by trained operators.</p>

        <h3>Preventive Maintenance</h3>
        <p>Scheduled servicing of pumps, valves, instruments, and electrical systems to prevent failures.</p>

        <h3>Spare Parts Management</h3>
        <p>Maintaining inventory of critical components to minimize downtime.</p>

        <h3>Operator Training</h3>
        <p>Continuous training programs ensuring competent operation and emergency response.</p>

        <h2>Common Operational Challenges</h2>
        <p>Our experience addresses typical issues:</p>
        <ul>
          <li><strong>Raw Water Variability:</strong> Seasonal changes requiring process adjustments</li>
          <li><strong>Chemical Supply:</strong> Ensuring consistent availability of treatment chemicals</li>
          <li><strong>Equipment Failures:</strong> Preventive maintenance and spare parts management</li>
          <li><strong>Power Reliability:</strong> Backup generators and alternative energy sources</li>
          <li><strong>Operator Skill Levels:</strong> Training and standard operating procedures</li>
        </ul>

        <h2>Community Water Systems</h2>
        <p>Small-scale treatment for rural communities:</p>
        <ul>
          <li>Simplified treatment processes appropriate for local capacity</li>
          <li>Robust, low-maintenance equipment</li>
          <li>Community involvement in operation and maintenance</li>
          <li>Sustainable financing models</li>
          <li>Technical support and monitoring</li>
        </ul>

        <h2>Industrial Water Treatment</h2>
        <p>Specialized systems for manufacturing processes:</p>
        <ul>
          <li>Process water meeting specific quality requirements</li>
          <li>Boiler feed water treatment</li>
          <li>Cooling water systems</li>
          <li>Wastewater treatment and recycling</li>
          <li>Integration with production processes</li>
        </ul>

        <h2>Emergency Preparedness</h2>
        <p>Plans for maintaining service during crises:</p>
        <ul>
          <li>Backup equipment and redundant systems</li>
          <li>Emergency chemical supplies</li>
          <li>Alternative power sources</li>
          <li>Response procedures for contamination events</li>
          <li>Communication protocols with authorities and consumers</li>
        </ul>

        <h2>Sustainability Considerations</h2>
        <p>Long-term viability requires holistic planning:</p>
        <ul>
          <li><strong>Environmental Protection:</strong> Minimizing impact on water sources</li>
          <li><strong>Resource Conservation:</strong> Efficient use of chemicals, energy, and water</li>
          <li><strong>Financial Sustainability:</strong> Appropriate tariff structures and cost recovery</li>
          <li><strong>Institutional Capacity:</strong> Strong management and governance</li>
          <li><strong>Community Engagement:</strong> Stakeholder participation and support</li>
        </ul>

        <h2>Technology Trends</h2>
        <p>Emerging technologies improving treatment:</p>
        <ul>
          <li>Membrane filtration (ultrafiltration, nanofiltration)</li>
          <li>Advanced oxidation processes</li>
          <li>Sensor technology and real-time monitoring</li>
          <li>Artificial intelligence for process optimization</li>
          <li>Mobile applications for remote monitoring</li>
        </ul>

        <h2>Plenco's Comprehensive Services</h2>
        <p>We provide end-to-end solutions:</p>
        <ul>
          <li>Feasibility studies and needs assessment</li>
          <li>Detailed engineering design</li>
          <li>Equipment procurement and installation</li>
          <li>Commissioning and performance testing</li>
          <li>Operator training programs</li>
          <li>Ongoing technical support and maintenance</li>
        </ul>

        <h2>Conclusion</h2>
        <p>Water treatment infrastructure is critical for public health and economic development. Plenco's expertise in designing, installing, and maintaining treatment facilities ensures communities and industries have reliable access to safe, clean water. Our commitment to quality, sustainability, and local capacity building makes us the partner of choice for water treatment projects across East Africa.</p>

        <p><strong>Planning a water treatment project?</strong> Contact our infrastructure development team for a comprehensive assessment and customized solution that meets your community's or facility's unique needs.</p>
      `
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/20 rounded-full mb-4">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Blog Post</h2>
          <p className="text-gray-600">Please wait while we fetch the article for you...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error || !post) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">
            {error ? error : "The blog post you're looking for doesn't exist."}
          </p>
          <Link to="/blog" className="text-primary hover:text-primary-700 font-semibold">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <article className="bg-white">
      <SEO 
        title={`${post.title} | Plenco Blog`}
        description={post.excerpt}
        keywords={post.tags.join(', ') + ', plumbing Uganda, construction Kampala'}
        canonicalUrl={`https://www.plencoplumbingltd.com/blog/${slug}`}
        ogImage={post.featured_image}
        type="article"
        author={post.author}
        publishedTime={post.published_at}
        modifiedTime={post.published_at}
        section={post.category}
        tags={post.tags}
      />
      
      <style>{`
        .article-content {
          color: #374151;
          line-height: 1.8;
        }
        
        .article-content h2 {
          font-size: 2.5rem;
          font-weight: 900;
          color: #035e85;
          margin-top: 4rem;
          margin-bottom: 2rem;
          padding-bottom: 1rem;
          border-bottom: 4px solid #035e8520;
          position: relative;
        }
        
        .article-content h2::before {
          content: '';
          position: absolute;
          left: 0;
          bottom: -4px;
          width: 80px;
          height: 4px;
          background: #035e85;
        }
        
        .article-content h3 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #1f2937;
          margin-top: 2.5rem;
          margin-bottom: 1.5rem;
          padding-left: 1rem;
          border-left: 4px solid #035e85;
        }
        
        .article-content p {
          font-size: 1.125rem;
          line-height: 2;
          color: #4b5563;
          margin-bottom: 1.5rem;
        }
        
        .article-content ul,
        .article-content ol {
          margin: 2rem 0;
          padding-left: 0;
          list-style: none;
        }
        
        .article-content ul li,
        .article-content ol li {
          font-size: 1.125rem;
          line-height: 1.8;
          color: #4b5563;
          margin-bottom: 1rem;
          padding-left: 2rem;
          position: relative;
          background: #f9fafb;
          padding: 1rem 1rem 1rem 3rem;
          border-radius: 0.5rem;
          border-left: 3px solid #035e85;
        }
        
        .article-content ul li::before {
          content: '✓';
          position: absolute;
          left: 1rem;
          color: #035e85;
          font-weight: bold;
          font-size: 1.25rem;
        }
        
        .article-content ol {
          counter-reset: item;
        }
        
        .article-content ol li::before {
          content: counter(item);
          counter-increment: item;
          position: absolute;
          left: 1rem;
          color: white;
          background: #035e85;
          width: 1.75rem;
          height: 1.75rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 0.875rem;
        }
        
        .article-content strong {
          color: #111827;
          font-weight: 800;
          background: linear-gradient(to right, #fef3c7, transparent);
          padding: 0.125rem 0.25rem;
          border-radius: 0.25rem;
        }
        
        .article-content a {
          color: #035e85;
          font-weight: 600;
          text-decoration: none;
          border-bottom: 2px solid #035e8530;
          transition: all 0.2s;
        }
        
        .article-content a:hover {
          color: #024a6a;
          border-bottom-color: #035e85;
        }
      `}</style>
      {/* Hero Section with Background Image */}
      <section className="relative h-96 bg-gray-900 pt-24 sm:pt-28">
        <div className="absolute inset-0">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center pt-8">
          <div>
            <Link 
              to="/blog" 
              className="inline-flex items-center text-white hover:text-primary-200 mb-4 font-semibold"
            >
              <ArrowLeftIcon className="w-5 h-5 mr-2" />
              Back to Blog
            </Link>
            <div className="mb-4">
              <span className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-bold">
                {post.category}
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center space-x-6 text-white text-sm">
              <div className="flex items-center space-x-2">
                <CalendarIcon className="h-5 w-5" />
                <time dateTime={post.published_at}>
                  {new Date(post.published_at).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </time>
              </div>
              <div className="flex items-center space-x-2">
                <ClockIcon className="h-5 w-5" />
                <span>{post.read_time}</span>
              </div>
              <div className="flex items-center space-x-2">
                <UserIcon className="h-5 w-5" />
                <span>{post.author}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Share Buttons */}
          <div className="mb-12 pb-8 border-b-2 border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center space-x-3">
                <TagIcon className="h-6 w-6 text-primary flex-shrink-0" />
                <div className="flex flex-wrap gap-3">
                  {(() => {
                    const tagsArray = Array.isArray(post.tags) ? post.tags : (typeof post.tags === 'string' ? JSON.parse(post.tags) : []);
                    return tagsArray.map((tag: string, idx: number) => (
                      <span 
                        key={idx} 
                        className="text-sm font-semibold bg-primary/10 text-primary px-4 py-2 rounded-full hover:bg-primary hover:text-white transition-colors cursor-pointer"
                      >
                        {tag}
                      </span>
                    ));
                  })()}
                </div>
              </div>
              <button className="flex items-center space-x-2 text-gray-600 hover:text-primary font-semibold">
                <ShareIcon className="h-5 w-5" />
                <span className="text-sm">Share Article</span>
              </button>
            </div>
          </div>

          {/* Article Body */}
          <div 
            className="article-content space-y-8"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* Call to Action */}
          <div className="mt-20 bg-gradient-to-br from-primary to-primary-700 rounded-2xl p-10 md:p-12 text-white shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-black mb-6">Need Expert Assistance?</h3>
            <p className="text-primary-100 mb-8 text-lg leading-relaxed">
              Our team of specialists is ready to help you with your {post.category.toLowerCase()} project. 
              Get in touch for a free consultation and detailed quotation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center bg-white text-primary px-8 py-4 rounded-lg font-bold hover:bg-primary-50 transition-all duration-300 shadow-lg hover:shadow-xl text-lg"
              >
                Contact Us Today
              </Link>
              <Link
                to="/projects"
                className="inline-flex items-center justify-center bg-primary-800 text-white px-8 py-4 rounded-lg font-bold hover:bg-primary-900 transition-all duration-300 border-2 border-white/30 text-lg"
              >
                View Our Projects
              </Link>
            </div>
          </div>

          {/* Author Info */}
          <div className="mt-16 pt-10 border-t-2 border-gray-200">
            <div className="flex items-start space-x-6">
              <div className="flex-shrink-0">
                <div className="w-20 h-20 bg-gradient-to-br from-primary to-primary-700 rounded-full flex items-center justify-center shadow-lg">
                  <UserIcon className="w-10 h-10 text-white" />
                </div>
              </div>
              <div>
                <h4 className="text-xl font-black text-gray-900 mb-3">{post.author}</h4>
                <p className="text-gray-600 text-lg leading-relaxed">
                  Plenco's expert team brings decades of combined experience in construction, plumbing,
                  and infrastructure development across Uganda and East Africa.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Posts or CTA */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-3xl font-black text-gray-900 mb-4">
            <span className="text-primary">Explore</span> More Articles
          </h3>
          <p className="text-gray-600 mb-8">
            Continue learning about construction-first, plumbing, and infrastructure solutions
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-lg font-bold hover:bg-primary-700 transition-colors shadow-lg"
          >
            View All Blog Posts
          </Link>
        </div>
      </section>
    </article>
  );
};

export default BlogPost;
